'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';

export type ProductPriceProps = {
  product: Product;
  className?: {
    main?: string;
    discount?: string;
    discountWrapper?: string;
    price?: string;
    priceAfterDiscount?: string;
    priceBeforeDiscount?: string;
  };
};

export function ProductPrice({ product, className }: ProductPriceProps) {
  const templateSettings = useGlobalStore((s) => s.templateSettings);
  const generalLayout = useGlobalStore((s) => s.generalLayout);
  const currency = generalLayout.currency;
  const currencySettings = generalLayout.currency_settings;
  const { t } = useTranslations();

  // const customer_discount = useGlobalStore(state => state.config.customer?.discount ?? 0);

  const me = useCustomerStore((state) => state.me);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });

  const unit = templateSettings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';

  if (priceDisplay.priceOnRequest) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-between gap-1',
        'ea-product-price',
        className?.main,
      )}
    >
      <div className="flex flex-col items-center">
        {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
          <p className="whitespace-nowrap text-sm text-muted-foreground">
            {`${t('text1926')} ${formatPrice(
              priceDisplay.mocPrice,
              currency,
              currencySettings,
            )}${unit}`}
          </p>
        ) : null}
        <div className={cn('flex items-baseline', 'ea-product-price__price', className?.price)}>
          <div
            data-ea-visual-tag="h3"
            className={cn(
              'whitespace-nowrap text-lg font-medium leading-[115%] tracking-tight',
              'ea-product-price__price-after-discount',
              priceDisplay.showPreviousPrice ? 'text-primary' : 'text-black',
              className?.priceAfterDiscount,
            )}
          >
            {formatPrice(priceDisplay.current, currency, currencySettings)} {unit}
          </div>
          {priceDisplay.showPreviousPrice ? (
            <del
              className={cn(
                'ml-3 whitespace-nowrap text-sm tracking-tight',
                'ea-product-price__price-before-discount',
                className?.priceBeforeDiscount,
              )}
            >{`${formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}${unit}`}</del>
          ) : null}
        </div>
        {templateSettings?.product_teaser?.price_without_vat?.enabled && (
          <p>
            {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
            {formatPrice(priceDisplay.secondary, currency, currencySettings)} {unit}{' '}
            {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
          </p>
        )}
      </div>
    </div>
  );
}
